import React, { useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Typography, Box } from '@mui/material'
import 'date-fns'
import { useIntl } from 'react-intl'
import esLocale from 'date-fns/locale/es'
import { getISOStringInitDate } from '../../core/utils/dateUtils'

/**
 * @callback OnDateChangeCallback
 * @param {string} isoDateString - La nueva fecha seleccionada, formateada como una cadena ISO.
 * @param {'fecha_desde' | 'fecha_hasta'} fieldName - El nombre del campo que ha cambiado ('fecha_desde' o 'fecha_hasta').
 */

/**
 * @typedef {object} DateRangePickerProps
 * @property {Date | string | null} startDate - La fecha de inicio del rango.
 * @property {Date | string | null} endDate - La fecha de fin del rango.
 * @property {OnDateChangeCallback} onChange - La función callback que se ejecuta cuando una de las fechas cambia.
 * @property {boolean} [dateChanged] - Si es `true`, habilita la visualización de un mensaje de advertencia cuando el usuario modifica una de las fechas.
 */

/**
 * Un componente de UI que renderiza dos selectores de fecha (`DatePicker`) para definir un rango de fechas (inicio y fin).
 *
 * Está diseñado para trabajar de forma controlada, recibiendo las fechas de inicio y fin como props.
 * La lógica interna asegura que la fecha de inicio no pueda ser posterior a la de fin, y viceversa.
 * Las fechas seleccionadas se devuelven como cadenas en formato ISO a través del callback `onChange`.
 * Opcionalmente, puede mostrar un mensaje de advertencia si las fechas son modificadas.
 *
 * @component
 * @example
 * const MyComponentWithDateRange = () => {
 * const [dates, setDates] = useState({
 * startDate: new Date(),
 * endDate: new Date('2025-12-31'),
 * });
 *
 * const handleDateChange = (isoDate, fieldName) => {
 * const key = fieldName === 'fecha_desde' ? 'startDate' : 'endDate';
 * setDates(prev => ({ ...prev, [key]: isoDate }));
 * };
 *
 * // Este componente debe estar envuelto en un <IntlProvider>.
 * return (
 * <DateRangePicker
 * startDate={dates.startDate}
 * endDate={dates.endDate}
 * onChange={handleDateChange}
 * dateChanged={true} // Habilita el mensaje de advertencia al cambiar
 * />
 * );
 * };
 *
 * @param {DateRangePickerProps} props - Las props del componente DateRangePicker.
 */
export const DateRangePicker = ({ startDate, endDate, onChange, dateChanged }) => {
  const { formatMessage } = useIntl()
  const [dateHasChanged, setDateHasChanged] = useState(false)
  const [openStart, setOpenStart] = useState(false)
  const [openEnd, setOpenEnd] = useState(false)

  const handleChange = (date, name) => {
    if (date && !isNaN(date.getTime())) {
      setDateHasChanged(true)
      onChange(getISOStringInitDate(date), name)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
      <Box display="flex" justifyContent="flex-end">
        <Box mr={'1.5em'}>
          <DatePicker
            label="Fecha inicio"
            value={startDate}
            maxDate={endDate ? endDate : null}
            onChange={(date) => handleChange(date, 'fecha_desde')}
            open={openStart}
            onOpen={() => setOpenStart(true)}
            onClose={() => setOpenStart(false)}
            slotProps={{
              textField: {
                variant: 'standard',
                sx: { width: '180px', cursor: 'pointer' },
                onClick: () => setOpenStart(true),
                InputProps: {
                  readOnly: true,
                },
              },
              openPickerIcon: { sx: { display: 'none' } },
            }}
          />
        </Box>
        <DatePicker
          label="Fecha fin"
          value={endDate}
          minDate={startDate ? startDate : null}
          onChange={(date) => handleChange(date, 'fecha_hasta')}
          open={openEnd}
          onOpen={() => setOpenEnd(true)}
          onClose={() => setOpenEnd(false)}
          slotProps={{
            textField: {
              variant: 'standard',
              sx: { width: '180px', cursor: 'pointer' },
              onClick: () => setOpenEnd(true),
              InputProps: {
                readOnly: true,
              },
            },
            openPickerIcon: { sx: { display: 'none' } },
          }}
        />
      </Box>
      {dateChanged && dateHasChanged ? (
        <Box mt={'1em'}>
          <Typography
            variant="subtitle2"
            display="inline"
            color="error"
            style={{ verticalAlign: 'super' }}
          >
            {formatMessage({ id: 'pages.public.offer.dialogs.general.terms.warning' })}
          </Typography>
        </Box>
      ) : null}
    </LocalizationProvider>
  )
}
